import React, { useContext, useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import debounce from "debounce"
import SearchService from "@src/services/content/search"
import { LayerContext } from "@src/context/layer-context"
import Link from "@src/components/core-link"
import SearchBar from "./reusable/search"
import SearchMenuSlider from "./navbar/search-menu-slider"
import LocationPin from "./reusable/svgs/location-pin"
import Map from "./reusable/svgs/map"
import File from "./reusable/svgs/file"
import DottedPath from "./reusable/svgs/dotted-path"
import SearchInBar from "./reusable/svgs/search-in-bar"
import SearchMobile from "./reusable/svgs/search-mobile"
import LinksHolder from "./reusable/links-holder"

const SearchResults = styled.div`
  position: relative;
  top: 8px;
  border: 1px solid ${props => props.theme.lightGray};
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  & div {
    flex: 0 0 auto;
  }
  border-radius: 24px !important;
  border: 1px solid #f2f4f7;
  background: #fff;
  box-shadow:
    0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px -7px 6px -2px rgba(16, 24, 40, 0.03);

  @media (max-width: 1200px) {
    display: none;
  }
`

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  @media (max-width: 1200px) {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }
  max-height: 50px;
`

const SearchHeader = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  padding-bottom: 20px;
  min-height: max-content;
`

const HeaderItem = styled.div`
  color: #757575;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0.5px;
  padding: 0 6px;
  min-width: max-content;
  min-height: max-content;
  min-height: 26px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  ${props =>
    props.$active &&
    css`
      border-bottom: 1px solid #1f1f23;
      color: #1f1f23;
    `}
`

const HeaderItemFiller = styled.div`
  width: 50%;
  min-height: 26px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`

const IconHolder = styled.div`
  border-radius: 6px;
  background: #f0f0f0;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 48px;
  min-height: 48px;
`

const LinkStyle = styled(Link)`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  text-decoration: none;
  color: #1f1f23;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.5px;

  &:hover {
    color: #1f1f23;
    border-radius: 4px;
    background: #fafafa;
  }
`

const SearchIconHolder = styled.div`
  border-radius: 6px;
  background: #daece4;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
`

const SearchMore = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: #2b4346;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  width: 100%;
  letter-spacing: 0.5px;
`

const Line = styled.div`
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`

const MobileSearch = styled.div`
  width: 44px;
  height: 44px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  display: none;
  @media (max-width: 1200px) {
    display: flex;
  }
  @media (max-width: 1000px) {
    height: auto;
  }
`

const SiteSearch = ({ className }) => {
  const Layers = useContext(LayerContext)
  const [isOpen, setIsOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [results, setResults] = useState(null)
  const [tours, setTours] = useState(null)
  const [trails, setTrails] = useState(null)
  const [stories, setStories] = useState(null)
  const [searching, setSearching] = useState(false)
  const [active, setActive] = useState("Tours")
  const [link, setLink] = useState("/tours")
  const [cta, setCta] = useState("See all tours & locations")
  // New state to indicate if more items are available
  const [hasMore, setHasMore] = useState(true)

  // Update the "results" and header info based on active category
  useEffect(() => {
    switch (active) {
      case "Tours":
        setResults(tours)
        setLink("/tours")
        setCta("See all tours & locations")
        break
      case "Trail guides":
        setResults(trails)
        setLink("/route-guides")
        setCta("See all Trail guides")
        break
      case "Stories":
        setResults(stories)
        setLink("/blog")
        setCta("See all stories")
        break
      default:
        setResults(null)
    }
  }, [tours, trails, stories, active])

  const searchRef = useRef({
    lastQuery: null,
    lastQueryPage: 1,
    query: (query, page = 1) => {
      page = page && parseInt(page) > 1 ? parseInt(page) : 1
      searchRef.current.lastQuery = query
      searchRef.current.lastQueryPage = page

      if (!query) {
        searchRef.current.lastQueryPage = 1
        setSearching(false)
        setResults(null)
        setIsOpen(false)
        setHasMore(true)
        return
      }
      setIsOpen(true)
      setSearching(true)

      Promise.all([
        SearchService.searchBlogPosts(query, page),
        SearchService.searchRoutes(query, page),
        SearchService.searchTours(query, page),
      ])
        .then(([blogPosts, routes, toursData]) => {
          // When page > 1, append new items to existing state; otherwise, replace.
          if (page > 1) {
            setStories(prev =>
              prev
                ? { ...blogPosts, items: [...prev.items, ...blogPosts.items] }
                : blogPosts,
            )
            setTrails(prev =>
              prev
                ? { ...routes, items: [...prev.items, ...routes.items] }
                : routes,
            )
            setTours(prev =>
              prev
                ? { ...toursData, items: [...prev.items, ...toursData.items] }
                : toursData,
            )
          } else {
            setStories(blogPosts)
            setTrails(routes)
            setTours(toursData)
          }

          let fetchedCount = 0
          if (active === "Tours") {
            fetchedCount = toursData.items.length
          } else if (active === "Trail guides") {
            fetchedCount = routes.items.length
          } else if (active === "Stories") {
            fetchedCount = blogPosts.items.length
          }
          setHasMore(fetchedCount === 10)
        })
        .catch(error =>
          console.error("Error in searchWebsite request: ", error),
        )
        .finally(() => {
          setSearching(false)
        })
    },

    queryDebounce: debounce(
      (query, page) => searchRef.current.query(query, page),
      250,
    ),
  })

  useEffect(() => {
    if (isOpen) {
      Layers.open({ layerId: "header-search", onClose: handleLayerClosed })
    } else {
      Layers.close("header-search")
    }
  }, [isOpen, Layers])

  const handleLayerClosed = () => {
    setIsOpen(false)
  }

  const handleSearchChange = event => {
    searchRef.current.queryDebounce(event.target.value, 1)
  }
  const handleSearchFocus = event => {
    if (searchRef.current.lastQuery !== event.target.value) {
      if (event.target.value) {
        searchRef.current.query(event.target.value, 1)
      } else {
        searchRef.current.lastQuery = ""
        setSearching(false)
        setResults(null)
      }
    } else if (searchRef.current.lastQuery) {
      setIsOpen(true)
    }
  }

  const handleScrollFrame = values => {
    console.log("handleScrollFrame")
    const { scrollTop, scrollHeight, clientHeight } = values
    const threshold = 50
    if (
      scrollTop + clientHeight >= scrollHeight - threshold &&
      !searching &&
      hasMore
    ) {
      const nextPage = searchRef.current.lastQueryPage + 1
      searchRef.current.query(searchRef.current.lastQuery, nextPage)
    }
  }

  return (
    <>
      <SearchWrapper
        className={`${className ? className : ""}${isOpen ? " active" : ""}`}
      >
        <SearchBar
          placeholder="Where do you want to go?"
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
        />
        <MobileSearch onClick={() => setIsMenuOpen(true)}>
          <SearchMobile />
        </MobileSearch>
        <SearchMenuSlider
          isOpen={isMenuOpen}
          setIsOpen={setIsMenuOpen}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          placeholder="Where do you want to go?"
        >
          <>
            <SearchHeader>
              <HeaderItem
                $active={active === "Tours"}
                onClick={() => setActive("Tours")}
              >
                Tours
              </HeaderItem>
              <HeaderItem
                $active={active === "Trail guides"}
                onClick={() => setActive("Trail guides")}
              >
                Trail guides
              </HeaderItem>
              <HeaderItem
                $active={active === "Stories"}
                onClick={() => setActive("Stories")}
              >
                Stories
              </HeaderItem>
              <HeaderItemFiller />
            </SearchHeader>
            {results && results.items ? (
              results.items.length ? (
                <>
                  <LinksHolder onScrollFrame={handleScrollFrame}>
                    {results.items.map((item, index) => (
                      <LinkStyle key={`search-results-${index}`} to={item.url}>
                        <IconHolder>
                          {(item.type === "tour_region" ||
                            item.type === "route_region") && <LocationPin />}
                          {item.type === "route" && <Map />}
                          {item.type === "tour" && <DottedPath />}
                          {item.type === "blog_post" && <File />}
                        </IconHolder>
                        {item.title}
                      </LinkStyle>
                    ))}
                    {searching && <span>Searching...</span>}
                  </LinksHolder>
                </>
              ) : (
                <span>No results found.</span>
              )
            ) : searching ? (
              <span>Searching...</span>
            ) : null}
            <Line />
            <LinkStyle to={link}>
              <SearchMore>
                <SearchIconHolder>
                  <SearchInBar />
                </SearchIconHolder>
                {cta}
              </SearchMore>
            </LinkStyle>
          </>
        </SearchMenuSlider>
        {isOpen && (results || searching) ? (
          <SearchResults className="results">
            <SearchHeader>
              <HeaderItem
                $active={active === "Tours"}
                onClick={() => setActive("Tours")}
              >
                Tours
              </HeaderItem>
              <HeaderItem
                $active={active === "Trail guides"}
                onClick={() => setActive("Trail guides")}
              >
                Trail guides
              </HeaderItem>
              <HeaderItem
                $active={active === "Stories"}
                onClick={() => setActive("Stories")}
              >
                Stories
              </HeaderItem>
              <HeaderItemFiller />
            </SearchHeader>
            {results && results.items ? (
              results.items.length ? (
                <>
                  {/* Also add the onScrollFrame prop here */}
                  <LinksHolder onScrollFrame={handleScrollFrame}>
                    {results.items.map((item, index) => (
                      <LinkStyle key={`search-results-${index}`} to={item.url}>
                        <IconHolder>
                          {(item.type === "tour_region" ||
                            item.type === "route_region") && <LocationPin />}
                          {item.type === "route" && <Map />}
                          {item.type === "tour" && <DottedPath />}
                          {item.type === "blog_post" && <File />}
                        </IconHolder>
                        {item.title}
                      </LinkStyle>
                    ))}
                    {searching && <span>Searching...</span>}
                  </LinksHolder>
                </>
              ) : (
                <span>No results found.</span>
              )
            ) : searching ? (
              <span>Searching...</span>
            ) : null}
            <Line />
            <LinkStyle to={link}>
              <SearchMore>
                <SearchIconHolder>
                  <SearchInBar />
                </SearchIconHolder>
                {cta}
              </SearchMore>
            </LinkStyle>
          </SearchResults>
        ) : null}
      </SearchWrapper>
    </>
  )
}

SiteSearch.propTypes = {
  className: PropTypes.string,
}

export default SiteSearch
